import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import IconButton from 'LuxKit/Button/IconButton'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Sheet from 'components/utils/Sheet'

const Banner = styled(Sheet)`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'message close'
    'actions actions';
  gap: ${rem(8)};
  align-items: start;
`

const Message = styled(BodyText)`
  grid-area: message;
  min-width: 0;
`

const Actions = styled.div`
  grid-area: actions;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${rem(8)};
`

const CloseButton = styled(IconButton)`
  grid-area: close;
`

interface Props {
  onCreateNewChat: () => void
  onDismiss: () => void
  onFeedbackClick?: () => void
}

/**
 * Warning banner shown to a spoofed staff session that has opened a chat the
 * customer CAN see (created by the customer). Warns that anything sent here is
 * visible to the customer and offers to start a new, hidden chat instead.
 * Presentational — the parent decides visibility and supplies both callbacks.
 */
function CustomerVisibleChatBanner({ onCreateNewChat, onDismiss, onFeedbackClick }: Props) {
  const intl = useIntl()

  const closeLabel = intl.formatMessage({
    id: 'WYXh_8yLMKajGJmG4XY0',
    defaultMessage: 'close',
    description: 'Aria label for close button in modal header dialog window',
  })

  return (
    <Banner
      className="no-print"
      background="critical"
      paletteType="inverse"
      padding={8}
    >
      <Message variant="medium" weight="bold" colour="normal-contrast" align="center">
        <FormattedMessage
          id="dHpIzkluSmOu9NOrGKkO"
          defaultMessage="Warning: the customer will view any messages or modifications made to this chat"
          description="Warning banner shown to spoofed staff viewing a customer-visible Trip Planner chat"
        />
      </Message>
      <CloseButton kind="tertiary" variant="dark" size="small" onClick={onDismiss} aria-label={closeLabel}>
        <LineTimesIcon />
      </CloseButton>
      <Actions>
        {onFeedbackClick && (
          <TextButton kind="secondary" variant="dark" size="small" onClick={onFeedbackClick}>
            <FormattedMessage
              id="Oz0kSI7mDNM5yY1kjiXx"
              defaultMessage="Report issue"
              description="Button label on the customer-visible chat spoof banner that opens feedback reporting for agents"
            />
          </TextButton>
        )}
        <TextButton kind="secondary" variant="dark" size="small" onClick={onCreateNewChat}>
          <FormattedMessage
            id="Mb_3wMzOh8DwYXZWmo9n"
            defaultMessage="Create a new, secret chat"
            description="Button that starts a new hidden chat from the customer-visible chat warning banner"
          />
        </TextButton>
      </Actions>
    </Banner>
  )
}

export default CustomerVisibleChatBanner
